import { HarmonyContext } from './harmony/HarmonyContext.js';
import { ChordProgressionStrategyRegistry } from './harmony/ChordProgressionStrategyRegistry.js';
import { SectionPatternStrategyEngine } from './harmony/SectionPatternStrategyEngine.js';
import { pickChordInterval, pickPercussionStyle } from './harmony/GenreProfiles.js';

export class HarmonySystem {
  constructor() {
    this.ctx = new HarmonyContext();
    this.progressions = new ChordProgressionStrategyRegistry();
    this.patterns = new SectionPatternStrategyEngine();
    this._chordTimer = 0;
  }

  get root() { return this.ctx.root; }
  get rootIdx() { return this.ctx.rootIdx; }
  get scale() { return this.ctx.scale; }
  get scaleName() { return this.ctx.scaleName; }
  get octave() { return this.ctx.octave; }
  get tempo() { return this.ctx.tempo; }
  get genre() { return this.ctx.genre; }
  get timeSig() { return this.ctx.timeSig; }
  get beatsPerBar() { return this.ctx.beatsPerBar; }
  get swing() { return this.ctx.swing; }
  get beatDuration() { return this.ctx.beatDuration; }
  get chordProgression() { return this.ctx.chordProgression; }
  get chordIndex() { return this.ctx.chordIndex; }
  get chordInterval() { return this.ctx.chordInterval; }

  setPlanet(music, planetSeed) {
    this.ctx.setPlanetMusic(music);

    this._initializePlanetRandom(planetSeed ?? music.seed ?? music.root);
    this.ctx.planetDensity = 0.1 + this.ctx.rand() * 0.9;

    this.ctx.chordInterval = pickChordInterval(this.ctx, this.ctx.rand);
    this.ctx.percussionStyle = pickPercussionStyle(this.ctx, this.ctx.rand);

    this.ctx.chordProgression = this.progressions.build(this.ctx);
    this.ctx.chordIndex = 0;
    this._chordTimer = 0;
    this.ctx.globalBeat = 0;
  }

  update(dt) {
    this.ctx.globalBeat += dt / this.ctx.beatDuration;
    this._chordTimer += dt;
    if (this._chordTimer >= this.ctx.beatDuration * this.ctx.chordInterval) {
      this._chordTimer -= this.ctx.beatDuration * this.ctx.chordInterval;
      this.ctx.chordIndex = (this.ctx.chordIndex + 1) % this.ctx.chordProgression.length;
    }
  }

  getCurrentChord() { return this.ctx.getCurrentChord(); }
  getCurrentChordNotes(octave) { return this.ctx.getCurrentChordNotes(octave); }
  noteFromSemitone(semi, octave) { return this.ctx.noteFromSemitone(semi, octave); }
  noteFromDegree(degreeIndex, octave) { return this.ctx.noteFromDegree(degreeIndex, octave); }
  freqFromDegree(degreeIndex, octave) { return this.ctx.freqFromDegree(degreeIndex, octave); }
  getScaleNotes(octave) { return this.ctx.getScaleNotes(octave); }
  getCreatureNote(voice) { return this.ctx.getCreatureNote(voice); }
  getResponseNote(leaderDegree, responderOffset) { return this.ctx.getResponseNote(leaderDegree, responderOffset); }
  quantizeTime(time, subdivision = '16n') { return this.ctx.quantizeTime(time, subdivision); }
  getEffectNote(index, octave) { return this.ctx.getEffectNote(index, octave); }
  getPercussiveNote(variationIndex) { return this.ctx.getPercussiveNote(variationIndex); }

  generateBarPattern(roleId, baseDegree, octaveOffset, seed) {
    return this.patterns.generateBarPattern(this.ctx, roleId, baseDegree, octaveOffset, seed);
  }

  _hashSeed(seed) {
    if (typeof seed === 'number') return seed | 0;
    if (typeof seed === 'string') {
      let hash = 0x811c9dc5;
      for (let i = 0; i < seed.length; i++) {
        hash ^= seed.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
      }
      return hash >>> 0;
    }
    return 0;
  }

  _initializePlanetRandom(seed) {
    this.ctx.planetSeed = this._hashSeed(seed);
    let state = this.ctx.planetSeed;
    this.ctx.rand = () => {
      state = (state + 0x6d2b79f5) >>> 0;
      let t = state;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
}
